import { useState, useEffect } from 'react';
import { collection, query, limit, orderBy, onSnapshot, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import StatsCard from '../components/StatsCard';
import { Link } from 'react-router-dom';

function Dashboard() {
  const [stats, setStats] = useState({
    activeDrivers: 0,
    totalDrivers: 0,
    tripsToday: 0,
    revenueToday: 0,
    pendingDrivers: 0,
    totalUsers: 0,
  });
  
  const [recentTrips, setRecentTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState('Todos');

  useEffect(() => {
    let unsubscribers = [];
    let isMounted = true;

    const initializeDashboard = async () => {
      try {
        // 1. Fetch drivers to create a country map
        const driversSnapshot = await getDocs(collection(db, 'drivers'));
        const validDriverIds = new Set();
        let pendingCount = 0;
        
        const getCountryFromPhone = (phone) => {
          if (!phone) return 'Desconocido';
          if (phone.startsWith('+58')) return 'Venezuela';
          if (phone.startsWith('+57')) return 'Colombia';
          if (phone.startsWith('+51')) return 'Perú';
          if (phone.startsWith('+55')) return 'Brasil';
          if (phone.startsWith('+52')) return 'México';
          if (phone.startsWith('+1')) return 'República Dominicana';
          return 'Desconocido';
        };

        driversSnapshot.forEach(doc => {
          const data = doc.data();
          const driverCountry = data.country || getCountryFromPhone(data.phone);
          
          if (selectedCountry === 'Todos' || driverCountry === selectedCountry) {
            validDriverIds.add(doc.id);
            if (data.approvalStatus === 'pending') {
              pendingCount++;
            }
          }
        });

        if (!isMounted) return;
        
        setStats(prev => ({ ...prev, pendingDrivers: pendingCount }));

        // 2. Listen for active drivers from Firestore
        const driversLocRef = collection(db, 'driver_locations');
        const driversUnsub = onSnapshot(driversLocRef, (snapshot) => {
          let activeCount = 0;
          let totalCount = 0;
          snapshot.forEach((doc) => {
            if (!validDriverIds.has(doc.id) && selectedCountry !== 'Todos') return;
            
            totalCount++;
            const data = doc.data();
            
            let timestamp = 0;
            if (data.updatedAt) {
              timestamp = typeof data.updatedAt.toMillis === 'function' 
                ? data.updatedAt.toMillis() 
                : data.updatedAt;
            }
            const FIVE_MINUTES = 5 * 60 * 1000;
            const now = Date.now();
            
            if (timestamp > 0 && now - timestamp > FIVE_MINUTES) {
              return; 
            }

            if (data.status === 'available' || data.status === 'busy') {
              activeCount++;
            }
          });
          setStats(prev => ({ ...prev, activeDrivers: activeCount, totalDrivers: validDriverIds.size }));
        }, (error) => {
          console.error('Error fetching driver_locations:', error);
        });
        unsubscribers.push(driversUnsub);

        // 3. Listen for today's trips
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const tripsQuery = query(
          collection(db, 'trips'),
          where('createdAt', '>=', today),
          orderBy('createdAt', 'desc')
        );
        
        const tripsUnsubscribe = onSnapshot(tripsQuery, (snapshot) => {
          let todayCount = 0;
          let revenue = 0;
          const tripsList = [];
          
          snapshot.forEach(doc => {
            const data = doc.data();
            // Si hay un chofer asignado y estamos filtrando, verificamos que sea de este país
            if (selectedCountry !== 'Todos' && data.driverId && !validDriverIds.has(data.driverId)) {
               return; // Skip trips from drivers of other countries
            }
            
            todayCount++;
            if (data.status === 'completed' && data.agreedFare) {
              revenue += data.agreedFare;
            }
            if (tripsList.length < 5) {
              tripsList.push({ id: doc.id, ...data });
            }
          });
          
          setStats(prev => ({ 
            ...prev, 
            tripsToday: todayCount,
            revenueToday: revenue 
          }));
          setRecentTrips(tripsList);
        }, (error) => {
          console.error('Error fetching trips:', error);
        });
        unsubscribers.push(tripsUnsubscribe);

        // 4. Count total users (Users are global since they don't have country)
        const usersQuery = query(collection(db, 'users'), limit(500));
        getDocs(usersQuery).then(snapshot => {
          if (isMounted) {
            setStats(prev => ({ ...prev, totalUsers: snapshot.size }));
          }
        }).catch(() => {});

        setLoading(false);

      } catch (err) {
        console.error("Dashboard init error", err);
        setLoading(false);
      }
    };

    initializeDashboard();

    return () => {
      isMounted = false;
      unsubscribers.forEach(unsub => {
        if (typeof unsub === 'function') unsub();
      });
    };
  }, [selectedCountry]);

  const getStatusBadge = (status) => {
    const badges = {
      completed: <span className="badge badge-success">Completado</span>,
      searching: <span className="badge badge-warning">Buscando</span>,
      negotiating: <span className="badge badge-warning">Negociando</span>,
      driver_assigned: <span className="badge badge-info">Asignado</span>,
      driver_on_the_way: <span className="badge badge-info">En Camino</span>,
      in_progress: <span className="badge badge-info">En Curso</span>,
      in_trip: <span className="badge badge-info">En Viaje</span>,
      cancelled: <span className="badge badge-danger">Cancelado</span>,
      no_drivers: <span className="badge badge-danger">Sin Conductor</span>,
    };
    return badges[status] || <span className="badge">{status}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="dashboard-skeleton">
        <div className="skeleton-grid">
          {[1,2,3,4].map(i => <div key={i} className="skeleton-card" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page animate-fade-in">
      {/* Page Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 className="page-title">Dashboard General</h2>
          <p className="page-subtitle">Resumen de actividad en tiempo real</p>
        </div>
        <div className="header-right" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select 
            className="input-field" 
            style={{ minWidth: '150px' }}
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            {['Todos', 'Perú', 'Colombia', 'República Dominicana', 'Brasil', 'Venezuela', 'México'].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="live-indicator">
            <span className="live-dot" />
            <span>En Vivo</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="stats-grid">
        <StatsCard 
          title="Conductores Activos" 
          value={stats.activeDrivers} 
          icon="🏍️" 
          colorHint="var(--accent-green)"
          subtitle={`${stats.totalDrivers} registrados`}
        />
        <StatsCard 
          title="Viajes Hoy" 
          value={stats.tripsToday} 
          icon="📍" 
          colorHint="var(--accent-blue)"
        />
        <StatsCard 
          title="Ingresos Hoy" 
          value={formatCurrency(stats.revenueToday)} 
          icon="💰" 
          colorHint="var(--accent-amber)"
        />
        <StatsCard 
          title="Pasajeros Registrados" 
          value={stats.totalUsers} 
          icon="👥" 
          colorHint="var(--accent-cyan)"
        />
      </div>

      {/* Alerts Row */}
      {stats.pendingDrivers > 0 && (
        <div className="alert-banner">
          <div className="alert-icon">⚠️</div>
          <div className="alert-content">
            <strong>{stats.pendingDrivers} conductor{stats.pendingDrivers > 1 ? 'es' : ''} pendiente{stats.pendingDrivers > 1 ? 's' : ''}</strong> de aprobación
          </div>
          <Link to="/drivers" className="alert-action">
            Revisar Ahora →
          </Link>
        </div>
      )}

      {/* Recent Activity Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Viajes Recientes</h3>
          <Link to="/trips" className="card-action">
            Ver Todos →
          </Link>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Recogida</th>
                <th>Destino</th>
                <th>Tarifa</th>
                <th>Estado</th>
                <th>Hora</th>
              </tr>
            </thead>
            <tbody>
              {recentTrips.length > 0 ? recentTrips.map((trip) => (
                <tr key={trip.id}>
                  <td className="mono-text">{trip.id.slice(0,8)}</td>
                  <td className="truncate-cell" title={trip.pickupAddress}>{trip.pickupAddress || 'GPS Location'}</td>
                  <td className="truncate-cell" title={trip.destinationAddress}>{trip.destinationAddress || 'GPS Location'}</td>
                  <td className="font-medium">{formatCurrency(trip.fareActual || trip.agreedFare || trip.proposedFare || trip.fareEstimate)}</td>
                  <td>{getStatusBadge(trip.status)}</td>
                  <td className="text-secondary">
                    {trip.createdAt?.toDate ? new Date(trip.createdAt.toDate()).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'N/A'}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="empty-state">
                    <div className="empty-icon">📭</div>
                    <p>No hay viajes recientes hoy</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
